import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessAgreementHeaderActionsFragment,
  PerformanceProcessAgreementSignAsEmployeeDocument,
  PerformanceProcessAgreementSignAsManagerDocument,
  PerformanceProcessAgreementUnsignAsEmployeeDocument,
  PerformanceProcessAgreementUnsignAsManagerDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

interface PrimaryAction {
  label: string
  onClick: () => void
  disabled?: boolean
}

interface SecondaryAction extends PrimaryAction {
  variant?: 'outline' | 'critical'
}

interface UseActionsReturnType {
  primaryAction: PrimaryAction | undefined
  secondaryActions: SecondaryAction[]
}

// TODO: COPY - Action plan e-signature
export const useActions = (
  agreement: PerformanceProcessAgreementHeaderActionsFragment
): UseActionsReturnType => {
  const { openDialog } = useConfirmationDialog()
  const { access } = useCurrentSession()

  const [signAsManager] = useMutation(PerformanceProcessAgreementSignAsManagerDocument, {
    extractorFn: (data) => data?.performanceMutations?.signAsManagerPerformanceAgreement,
  })

  const [signAsEmployee] = useMutation(PerformanceProcessAgreementSignAsEmployeeDocument, {
    extractorFn: (data) => data?.performanceMutations?.signAsEmployeePerformanceAgreement,
  })

  const [unsignAsManager] = useMutation(PerformanceProcessAgreementUnsignAsManagerDocument, {
    extractorFn: (data) => data?.performanceMutations?.unsignAsManagerPerformanceAgreement,
  })

  const [unsignAsEmployee] = useMutation(PerformanceProcessAgreementUnsignAsEmployeeDocument, {
    extractorFn: (data) => data?.performanceMutations?.unsignAsEmployeePerformanceAgreement,
  })

  let primaryAction: PrimaryAction | undefined = undefined
  const secondaryActions: SecondaryAction[] = []

  const targetName = agreement.target?.access?.firstName
  const reviewerName = agreement.reviewer?.firstName
  const signerName = agreement.signer?.firstName

  const isLastSigner = Boolean(agreement.managerSignedAt || agreement.targetSignedAt)

  const unsignAsTargetAction: SecondaryAction = {
    label: 'Revert confirmation',
    variant: 'critical',
    onClick: () =>
      openDialog({
        title: 'Revert confirmation',
        description: 'Your action plan will remain open for edits until you re-confirm.',
        illustration: 'delete-cross',
        onConfirm: () =>
          unsignAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const unsignAsTargetForManagerAction: SecondaryAction = {
    label: `Revert ${targetName} confirmation`,
    variant: 'critical',
    onClick: () =>
      openDialog({
        title: `Revert ${targetName}'s confirmation`,
        description: 'Your action plan will remain open for edits until one of you re-confirm.',
        illustration: 'delete-cross',
        onConfirm: () =>
          unsignAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const unsignAsManagerAction: SecondaryAction = {
    label: 'Revert confirmation',
    variant: 'critical',
    onClick: () =>
      openDialog({
        title: 'Revert confirmation',
        description: 'Your action plan will remain open for edits until you re-confirm.',
        illustration: 'delete-cross',
        onConfirm: () =>
          unsignAsManager({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const signAsTargetAction: PrimaryAction = {
    label: 'Submit action plan',
    onClick: () => {
      const otherSignerName = signerName || reviewerName

      const documentGenerationLabel = agreement.process?.agreementsConfiguration?.esignatureEnabled
        ? `A document will be generated and both you and ${otherSignerName} must sign the PDF.`
        : 'A document will be generated.'

      const sideEffectLabel = isLastSigner
        ? documentGenerationLabel
        : `We'll notify ${otherSignerName} to confirm as well.`

      openDialog({
        title: 'Submit action plan',
        description: `Once the action plan is confirmed, you will no longer be able to edit or add anything to this action plan. ${sideEffectLabel}`,
        illustration: 'check',
        confirmLabel: i18n.t('common.confirm'),
        onConfirm: () =>
          signAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      })
    },
  }

  const signAsManagerAction: PrimaryAction = {
    label: 'Submit action plan',
    onClick: () => {
      const documentGenerationLabel = agreement.process?.agreementsConfiguration?.esignatureEnabled
        ? `A document will be generated and both you and ${targetName} must sign the PDF.`
        : 'A document will be generated.'

      const sideEffectLabel = isLastSigner
        ? documentGenerationLabel
        : `We'll notify ${targetName} to confirm as well.`

      openDialog({
        title: 'Submit action plan',
        description: `Once the action plan is confirmed, you will no longer be able to edit or add anything to this action plan. ${sideEffectLabel}`,
        illustration: 'check',
        confirmLabel: i18n.t('common.confirm'),
        onConfirm: () =>
          signAsManager({
            variables: {
              agreementId: agreement.id,
            },
          }),
      })
    },
  }

  if (agreement._policies?.unsignAsTarget.can) {
    const isTarget = agreement.target?.access?.id === access.id
    secondaryActions.push(isTarget ? unsignAsTargetAction : unsignAsTargetForManagerAction)
  }

  if (agreement._policies?.unsignAsManager.can) {
    secondaryActions.push(unsignAsManagerAction)
  }

  if (agreement._policies?.signAsTarget.can) {
    primaryAction = signAsTargetAction
  }

  if (agreement?._policies?.signAsManager.can) {
    primaryAction = signAsManagerAction
  }

  return { primaryAction, secondaryActions }
}
